/* eslint-disable max-lines */

import { action, computed, observable } from 'mobx';
import { AnalyticsStore, ContextStore, Store } from 'bernie-plugin-mobx';

import { getBexApiContext } from 'bernie-context';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { SerializedData } from 'bernie-core';

import { ILocalization } from '@shared-ui/localization-context';

import { BexApiSource } from 'sources/bex-api-source';

import {
  buildMultiItemContext,
  CartInfo,
  clearMixAndMatchCartFromStorage,
  CollectionBusinessModel,
  INCOMPATIBLE_RATEPLAN,
  loadCartFromStorage,
  MultiItemStoreStatus,
  saveCartInStorage,
  updateStatus,
} from './multi-item-store-util';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import {
  InCartProperty,
  MaxRoomsFailureReason,
  MultiItemContext,
  PackageType,
  PrepareCheckoutResponse,
} from 'models/multi-item/multi-item-types';

import { UitkBannerProps } from 'uitk-react-banner';

import { DateUtils } from 'utils/date-utils';

import {
  AddPropertyToCart,
  CarNaturalKeyInput,
  CarSearchCriteriaInput,
  CartPropertySearchContextInput,
  CartTtl,
  CheckoutOptionInput,
  CheckoutOptionType,
  DateInput,
  EmptyCart,
  FlightNaturalKeyInput,
  FlightsAdvancedFilterType,
  FlightsCabinClass,
  FlightSearchCriteriaInput,
  FlightsJourneyCriteriaInput,
  FlightsTripType,
  MoneyInput,
  MultiItemSearchContextInput,
  PackageType as BexApiPackageType,
  PrepareCheckoutByCartId,
  PropertyNaturalKeyInput,
  PropertySearchCriteriaInput,
  RemoveItemFromCart,
  RoomInput,
  Scalars,
  TravelerDetailsInput,
  TravelerDetailsType,
} from '__generated__/api';
import { cartItemToInCartProperty } from 'components/multi-item/cart-adapter';
import { getLocKeyFromBexApiDateTime } from 'components/multi-item/bex-api-date-time-to-time-loc-key';

type Uuid = Scalars['UUID'];

export const packageTypeMap: { [key in BexApiPackageType]: PackageType } = {
  HOTEL_CAR: PackageType.HC,
  FLIGHT_HOTEL: PackageType.FH,
  FLIGHT_HOTEL_CAR: PackageType.FHC,
  FLIGHT_CAR: PackageType.FC,
};

export const packageTypeToBexApiType: { [key in PackageType]: BexApiPackageType } = {
  HC: BexApiPackageType.HOTEL_CAR,
  FH: BexApiPackageType.FLIGHT_HOTEL,
  FHC: BexApiPackageType.FLIGHT_HOTEL_CAR,
  FC: BexApiPackageType.FLIGHT_CAR,
};

const emptyDate: DateInput = {
  year: 0,
  month: 0,
  day: 0,
};

export class MultiItemStore extends Store {
  private bexApiSource: BexApiSource;
  private l10n: ILocalization;

  constructor(bexApiSource: BexApiSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER, l10n: ILocalization) {
    super(state, logger);

    this.bexApiSource = bexApiSource;
    this.l10n = l10n;

    if (!this.isServerSide() && !this.isPackageShoppingPath()) {
      this.cartInfo = loadCartFromStorage();
    }
    this.setError = this.setError.bind(this);
    this.setGenericError = this.setGenericError.bind(this);
    this.isAllSoldOut = this.isAllSoldOut.bind(this);
    this.calculateTotalPrice = this.calculateTotalPrice.bind(this);
  }

  public hydrate(data: SerializedData): void {
    if (data) {
      delete data.bexApiSource;
      delete data.l10n;
      Object.assign(this, data);
    }
  }

  @observable
  public itemCount = 0;
  @observable
  public items: InCartProperty[] = [];
  @observable
  public status!: MultiItemStoreStatus;
  @observable
  public error!: UitkBannerProps;
  @observable
  public cartInfo!: CartInfo | null;
  @observable
  public errorMessage!: string;
  @observable
  public multiItemContext!: MultiItemContext | null;
  @observable
  public ttla!: string;
  @observable
  public pageLoading = false;
  public referrer!: string;
  public isMixMatchEnabled = false;

  @computed
  get multiItemSessionId() {
    return this.isPackageShoppingPath() ? this.multiItemContext?.id : null;
  }

  private mapCarTimeAndDateToDateTime(time: string, date: DateInput | null | typeof emptyDate = emptyDate) {
    return {
      hour: time ? Number(time.substring(0, 2)) : 0,
      minute: time ? Number(time.substring(2, 4)) : 0,
      second: 0,
      month: date?.month || 0,
      day: date?.day || 0,
      year: date?.year || 0,
    };
  }

  private getDefaultCarPickUpDropOffDateTime(date: DateInput | null | typeof emptyDate = emptyDate) {
    return {
      hour: 10,
      minute: 30,
      second: 0,
      month: date?.month || 0,
      day: date?.day || 0,
      year: date?.year || 0,
    };
  }

  public getCompressedCarTime(multiItemSearchContext: MultiItemSearchContextInput, dateTimeField: 'pickUpDateTime' | 'dropOffDateTime') {
    if (
      multiItemSearchContext &&
      multiItemSearchContext.cars &&
      multiItemSearchContext.cars.length &&
      multiItemSearchContext.cars[0].primary &&
      multiItemSearchContext.cars[0].primary[dateTimeField]
    ) {
      return getLocKeyFromBexApiDateTime(multiItemSearchContext.cars[0].primary[dateTimeField]);
    }

    return null;
  }

  public getFlightOrigin(multiItemSearchContext: MultiItemSearchContextInput) {
    if (
      multiItemSearchContext &&
      multiItemSearchContext.flights &&
      multiItemSearchContext.flights.length &&
      multiItemSearchContext.flights[0].primary &&
      multiItemSearchContext.flights[0].primary.journeyCriterias &&
      multiItemSearchContext.flights[0].primary.journeyCriterias.length &&
      multiItemSearchContext.flights[0].primary.journeyCriterias[0]
    ) {
      return multiItemSearchContext.flights[0].primary.journeyCriterias[0].origin;
    }

    return null;
  }

  public getFlightNonStop(multiItemSearchContext: MultiItemSearchContextInput) {
    return multiItemSearchContext?.flights?.[0]?.primary?.searchPreferences?.advancedFilters?.includes(FlightsAdvancedFilterType.NONSTOP);
  }

  public getFlightCabinClass(multiItemSearchContext: MultiItemSearchContextInput): string | null {
    return multiItemSearchContext?.flights?.[0]?.primary?.searchPreferences?.cabinClass ?? null;
  }

  public getFlightTripType(multiItemSearchContext: MultiItemSearchContextInput): string | null {
    return multiItemSearchContext?.flights?.[0]?.primary?.tripType ?? null;
  }

  public getInfantsInSeats(multiItemSearchContext: MultiItemSearchContextInput): string {
    const infantSeats = multiItemSearchContext?.flights?.[0]?.primary?.travelers?.filter(
      traveler => traveler?.type === TravelerDetailsType.INFANT_IN_SEAT
    );

    return Array.isArray(infantSeats) && infantSeats.length > 0 ? '1' : '0';
  }

  private buildMultiItemSearchContext(model: SearchCriteriaModel): MultiItemSearchContextInput {
    const startDate = model.startDate ? DateUtils.parseDateString(model.startDate) || emptyDate : emptyDate;
    const endDate = model.endDate ? DateUtils.parseDateString(model.endDate) || emptyDate : emptyDate;
    const rooms = model.rooms ? (model.rooms.map(room => room.toJSON()) as RoomInput[]) : [];

    const propertySearchCriteria = this.buildPropertySearchCriteria(startDate, endDate, model, rooms);

    const carsSearchCriteria =
      this.multiItemContext?.packageType && this.multiItemContext.packageType !== PackageType.FH
        ? this.buildCarsSearchCriteria(model, startDate, endDate)
        : null;
    const flightsSearchCriteria =
      this.multiItemContext?.packageType && this.multiItemContext.packageType !== PackageType.HC
        ? this.buildFlightsSearchCriteria(model, startDate, endDate)
        : null;

    return {
      properties: propertySearchCriteria,
      cars: carsSearchCriteria,
      flights: flightsSearchCriteria,
    };
  }

  private buildPropertySearchCriteria(
    startDate: DateInput | null,
    endDate: DateInput | null,
    model: SearchCriteriaModel,
    rooms: RoomInput[]
  ): PropertySearchCriteriaInput[] | null | undefined {
    let newStartDate = startDate || emptyDate;
    let newEndDate = endDate || emptyDate;

    if (model.isPartialStay) {
      newEndDate = model.partialStayEndDate ? DateUtils.parseDateString(model.partialStayEndDate) || emptyDate : newEndDate;
      newStartDate = model.partialStayStartDate ? DateUtils.parseDateString(model.partialStayStartDate) || emptyDate : newStartDate;
    }

    return [
      {
        primary: {
          dateRange: {
            checkInDate: newStartDate,
            checkOutDate: newEndDate,
          },
          destination: {
            regionName: model.destination || null,
            regionId: model.regionId || null,
          },
          rooms,
        },
      },
    ];
  }

  private buildCarsSearchCriteria(
    model: SearchCriteriaModel,
    startDate: DateInput | null,
    endDate: DateInput | null
  ): CarSearchCriteriaInput[] {
    return [
      {
        primary: {
          pickUpDateTime: model.pickUpTime
            ? this.mapCarTimeAndDateToDateTime(model.pickUpTime, startDate)
            : this.getDefaultCarPickUpDropOffDateTime(startDate),
          dropOffDateTime: model.dropOffTime
            ? this.mapCarTimeAndDateToDateTime(model.dropOffTime, endDate)
            : this.getDefaultCarPickUpDropOffDateTime(endDate),
          pickUpLocation: {
            regionId: model.regionId || null,
          },
        },
      },
    ];
  }

  private buildFlightsSearchCriteria(
    model: SearchCriteriaModel,
    startDate: DateInput | null,
    endDate: DateInput | null
  ): FlightSearchCriteriaInput[] {
    const flightTripType = model.tripType ? FlightsTripType[model.tripType as keyof typeof FlightsTripType] : FlightsTripType.ROUND_TRIP;

    return [
      {
        primary: {
          tripType: flightTripType,
          travelers: this.buildFlightsTravelers(model),
          journeyCriterias: this.buildFlightsJourneyCriteria(model, startDate, endDate, flightTripType),
          searchPreferences: this.buildFlightsSearchPreferences(model),
        },
      },
    ];
  }

  private buildFlightsSearchPreferences(model: SearchCriteriaModel) {
    return {
      cabinClass: this.getFlightsCabinClass(model.cabinClass),
      airline: model.airlineCode,
      advancedFilters: model.directFlights === 'true' ? [FlightsAdvancedFilterType.NONSTOP] : null,
    };
  }

  private buildFlightsJourneyCriteria(
    { origin, destination }: SearchCriteriaModel,
    startDate: DateInput | null,
    endDate: DateInput | null,
    flightTripType: FlightsTripType
  ) {
    const journeyCriterias: FlightsJourneyCriteriaInput[] = [];
    const realDestination = this.ttla ? this.ttla : destination;

    journeyCriterias.push({
      departureDate: startDate || emptyDate,
      origin: origin ?? SearchCriteriaModel.DEFAULTS.origin,
      destination: realDestination,
    });

    if (flightTripType === FlightsTripType.ROUND_TRIP) {
      journeyCriterias.push({
        departureDate: endDate || emptyDate,
        origin: realDestination,
        destination: origin ?? SearchCriteriaModel.DEFAULTS.origin,
      });
    }

    return journeyCriterias;
  }

  private getFlightsCabinClass(cabinClass?: string): FlightsCabinClass {
    return cabinClass ? FlightsCabinClass[cabinClass as keyof typeof FlightsCabinClass] : FlightsCabinClass.COACH;
  }

  private buildFlightsTravelers(model: SearchCriteriaModel) {
    const travelers: TravelerDetailsInput[] = [];
    model.rooms?.forEach(room => {
      for (let i = 0; i < room.adults; i++) {
        travelers.push({ age: null, type: TravelerDetailsType.ADULT });
      }

      if (room.children) {
        const children = room.children.filter(child => child.age >= 2);
        const infants = room.children.filter(child => child.age < 2);

        if (children) {
          children.forEach(({ age }) => travelers.push({ age, type: TravelerDetailsType.CHILD }));
        }

        if (infants) {
          const type = model.infantsInSeats === '1' ? TravelerDetailsType.INFANT_IN_SEAT : TravelerDetailsType.INFANT_IN_LAP;
          infants.forEach(({ age }) => travelers.push({ age, type }));
        }
      }
    });

    return travelers;
  }

  @action
  public async selectProducts(
    context: ContextStore,
    sessionId: string,
    priceToken: string,
    properties: PropertyNaturalKeyInput[],
    flights: FlightNaturalKeyInput[],
    cars: CarNaturalKeyInput[],
    dealMarker: string | undefined
  ) {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCHING);
    this.pageLoading = true;

    const omitTypename: (key: any, value: any) => any = (key, value) => (key === '__typename' ? undefined : value);

    return this.bexApiSource
      .selectProducts({
        context: getBexApiContext(context),
        sessionId,
        priceToken,
        properties: JSON.parse(JSON.stringify(properties), omitTypename),
        flights,
        cars,
        dealMarker,
      })
      .then(({ data, errors }) => {
        if (
          data?.multiItemShopping.selectProducts.__typename === 'MultiItemSearchContextCreatedResponse' &&
          data?.multiItemShopping.selectProducts.redirectUrl
        ) {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.SELECT_PRODUCTS_SUCCESS);

          return Promise.resolve(data.multiItemShopping.selectProducts.redirectUrl);
        } else if (
          data?.multiItemShopping.selectProducts.__typename === 'MultiItemSearchContextErrorResponse' &&
          data?.multiItemShopping.selectProducts.message
        ) {
          this.errorMessage = data.multiItemShopping.selectProducts.message;
          this.pageLoading = false;

          return Promise.reject(errors || ['error response returned from selectProducts']);
        }
        this.pageLoading = false;

        return Promise.reject(errors || ['error while selecting products']);
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.SELECT_PRODUCTS_FAILURE, error);
        this.pageLoading = false;

        return Promise.reject(error);
      });
  }

  @action
  public async initiateMultiItemSession(context: ContextStore, model: SearchCriteriaModel) {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCHING);

    return this.bexApiSource
      .initiateMultiItemSession({
        context: getBexApiContext(context),
        searchContext: this.buildMultiItemSearchContext(model),
      })
      .then(({ data, errors }) => {
        if (
          data?.multiItemShopping.initiate.__typename === 'MultiItemSearchContextCreatedResponse' &&
          data?.multiItemShopping.initiate.shoppingContext.multiItem?.id &&
          data?.multiItemShopping.initiate.shoppingContext.multiItem?.packageType
        ) {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.INITIATE_SESSION_SUCCESS);
          const { id, packageType } = data.multiItemShopping.initiate.shoppingContext.multiItem;

          if (this.multiItemContext) {
            Object.assign(this.multiItemContext, {
              id,
              packageType: packageTypeMap[packageType],
            });
          }

          return Promise.resolve(id);
        } else if (
          data?.multiItemShopping.initiate.__typename === 'MultiItemSearchContextErrorResponse' &&
          data?.multiItemShopping.initiate.message
        ) {
          return Promise.reject(errors || ['error response returned from initiate']);
        }

        return Promise.reject(errors || ['error while initiating multiitem session']);
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.INITIATE_SESSION_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public async createMultiItemSession(context: ContextStore, model: SearchCriteriaModel) {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCHING);

    return this.bexApiSource
      .createMultiItemSession({
        context: getBexApiContext(context),
        multiItemSearchContext: this.buildMultiItemSearchContext(model),
      })
      .then(({ data, errors }) => {
        if (
          data &&
          data.createMultiItemSession &&
          data.createMultiItemSession.__typename === 'MultiItemSearchContextCreatedResponse' &&
          data.createMultiItemSession.shoppingContext &&
          data.createMultiItemSession.shoppingContext.multiItem
        ) {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.CREATE_SESSION_SUCCESS);

          const { id, packageType } = data.createMultiItemSession.shoppingContext.multiItem;
          if (this.multiItemContext) {
            Object.assign(this.multiItemContext, {
              id,
              packageType: packageTypeMap[packageType],
            });
          }

          return Promise.resolve(id);
        }

        return Promise.reject(errors || 'error while creating multiitem session');
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.CREATE_SESSION_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public async getCartDetails(context: ContextStore): Promise<InCartProperty[] | null> {
    if (!this.cartInfo || !this.cartInfo.id) {
      return null;
    }
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCHING);

    return this.bexApiSource
      .fetchCart({
        context: getBexApiContext(context),
        id: this.cartInfo.id,
      })
      .then(({ data: value }) => {
        if (!value || !value.cart || !value.cart.items) {
          return Promise.reject('no items returned');
        }
        this.itemCount = value.cart.itemCount;

        this.items = value.cart.items.map(cartItemToInCartProperty(this.isMixMatchEnabled));
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCH_SUCCESS);

        return Promise.resolve(this.items);
      })
      .catch(error => {
        if (this.logger && typeof this.logger.logEvent === 'function') {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCH_FAILURE, error);
        }
        if (error.toString().includes('CHECK_IN_DATE_IN_PAST_ERROR')) {
          this.resetCart();
        }

        return Promise.reject(error);
      });
  }

  @action
  public addPropertyToCart(
    context: ContextStore,
    propertyNaturalKeyInputs: PropertyNaturalKeyInput[],
    cartPropertySearchContextInput: CartPropertySearchContextInput,
    businessModel: CollectionBusinessModel,
    analytics?: AnalyticsStore,
    isNoCC?: boolean,
    newRoomIndex?: number
  ): Promise<AddPropertyToCart.mutation | undefined> | undefined {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY);

    return this.getOrCreateCart(context, businessModel, analytics, isNoCC)?.then(cartInfo => {
      if (cartInfo && cartInfo.type !== CollectionBusinessModel.NA && cartInfo.type !== businessModel) {
        return Promise.reject(INCOMPATIBLE_RATEPLAN);
      }

      if (this.isMixMatchEnabled && this.cartInfo) {
        this.cartInfo.type = businessModel;
      }
      const offerIndex =
        this.isMixMatchEnabled && newRoomIndex !== null && typeof newRoomIndex !== 'undefined' ? newRoomIndex : this.itemCount;

      return this.bexApiSource
        .addPropertyToCart({
          id: cartInfo.id,
          context: getBexApiContext(context),
          propertyOffer: {
            offerIndex,
            naturalKeys: propertyNaturalKeyInputs,
          },
          cartPropertySearchContextInput,
        })
        .then(({ data: response }) => {
          if (!response || !response.cartItems || !response.cartItems.addProperty) {
            Promise.reject('add property - failed to parse response');
          }
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY_SUCCESS);
          this.itemCount = response?.cartItems.addProperty.itemCount || 0;
          saveCartInStorage(this.cartInfo);

          return response;
        })
        .catch(error => {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY_FAILURE, error);

          return Promise.reject(error);
        });
    });
  }

  @action
  public removeItemFromCart(context: ContextStore, itemId: string): Promise<RemoveItemFromCart.mutation | undefined> {
    if (!this.cartInfo || !this.cartInfo.id) {
      this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_FAILURE, 'No Cart has been initialized yet');

      return Promise.reject('No Cart has been initialized yet');
    }
    const deletedIndex: number = this.items.findIndex(obj => obj.id === itemId);
    const deletedItem: InCartProperty = this.items[deletedIndex];
    this.items = [...this.items.slice(0, deletedIndex), ...this.items.slice(deletedIndex + 1)];
    this.itemCount--; // eslint-disable-line

    return this.bexApiSource
      .removeItemFromCart({
        cartId: this.cartInfo.id,
        context: getBexApiContext(context),
        itemId,
      })
      .then(({ data: response }) => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_SUCCESS);
        this.itemCount = response?.cartItems.remove.itemCount || 0;

        return Promise.resolve(response);
      })
      .catch(error => {
        this.items.splice(deletedIndex, 0, deletedItem);
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_FAILURE, error);
        this.itemCount++; // eslint-disable-line

        return Promise.reject(error);
      });
  }

  @action
  public emptyCart(context: ContextStore): Promise<EmptyCart.mutation | undefined> {
    if (!this.cartInfo || !this.cartInfo.id) {
      this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_FAILURE, 'No Cart has been initialized yet');

      return Promise.reject('No Cart has been initialized yet');
    }

    return this.bexApiSource
      .emptyCart({
        id: this.cartInfo.id,
        context: getBexApiContext(context),
      })
      .then(({ data: response }) => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_SUCCESS);
        this.itemCount = response?.cartItems.emptyCart.itemCount || 0;

        if (this.cartInfo) {
          this.cartInfo.type = this.getDefaultBusinessModel();
        }

        saveCartInStorage(this.cartInfo);

        return Promise.resolve(response);
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public async prepareCheckout(context: ContextStore, properties: [PropertyNaturalKeyInput]): Promise<PrepareCheckoutResponse> {
    return this.bexApiSource
      .prepareCheckout({
        context: getBexApiContext(context),
        cookies: '',
        properties,
      })
      .then(({ data: value }) => {
        if (!value || !value.prepareCheckout || !value.prepareCheckout.checkoutUrl) {
          return Promise.reject('bad response from prepare checkout');
        }
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_SUCCESS);

        return Promise.resolve({
          checkoutUrl: value.prepareCheckout.checkoutUrl,
        });
      })
      .catch(error => Promise.reject(error));
  }

  @action
  public async prepareCheckoutByCartId(context: ContextStore): Promise<PrepareCheckoutResponse | undefined> {
    if (!this.cartInfo || !this.cartInfo.id) {
      return Promise.reject('No Cart has been initialized yet');
    }
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_PROCESSING);
    const marketingChannelId = this.items[0] && this.items[0].marketingChannelId;
    const prepareCheckoutVariables: PrepareCheckoutByCartId.Variables = {
      context: getBexApiContext(context),
      id: this.cartInfo.id,
      totalPrice: this.calculateTotalPrice(),
      checkoutOptions: this.getCheckoutOptions(),
    };
    if (marketingChannelId) {
      prepareCheckoutVariables.marketingChannelId = marketingChannelId;
    }

    return this.bexApiSource
      .prepareCheckoutByCartId(prepareCheckoutVariables)
      .then(({ data: value }) => {
        if (value && value.prepareCheckoutByCartId && value.prepareCheckoutByCartId.failureReason) {
          const failureReason = value.prepareCheckoutByCartId.failureReason as PrepareCheckoutByCartId.MaxRoomsFailureInlineFragment;

          this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_FAILURE, 'max rooms', {
            message: failureReason.message,
            groupLink: failureReason.groupLinkMessage || '',
          });

          return;
        }

        if (!value || !value.prepareCheckoutByCartId || !value.prepareCheckoutByCartId.checkoutUrl) {
          return Promise.reject('bad response from prepare checkout');
        }
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_SUCCESS);

        return Promise.resolve({
          checkoutUrl: value.prepareCheckoutByCartId.checkoutUrl,
        });
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public removeUnavailableAndPrepareCheckout(context: ContextStore): Promise<PrepareCheckoutResponse | undefined> {
    return this.removeAllUnavailable(context).then(() => this.prepareCheckoutByCartId(context));
  }

  public removeAllUnavailable(context: ContextStore): Promise<(RemoveItemFromCart.mutation | undefined)[]> {
    if (!this.items) {
      return Promise.reject('There are no items in the cart');
    }

    return Promise.all(this.items.filter(item => !item.ratePlan.available).map(item => this.removeItemFromCart(context, item.id)));
  }

  public isAllSoldOut(): boolean {
    return this.items && !this.items.some(item => item.ratePlan && item.ratePlan.available);
  }

  public cartContainsUnavailItems(): boolean {
    return this.items && this.items.some(item => !item.ratePlan || !item.ratePlan.available);
  }

  public resetStatus() {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.IDLE);
  }

  public getOrCreateCart(
    context: ContextStore,
    cartType?: CollectionBusinessModel,
    analytics?: AnalyticsStore,
    isNoCC?: boolean
  ): Promise<CartInfo> | null {
    if (this.isServerSide()) {
      return null;
    }

    if (!this.cartInfo || !this.cartInfo.id) {
      this.cartInfo = loadCartFromStorage();
    }
    if (this.cartInfo === null) {
      if (isNoCC && this.itemCount === 0) {
        return Promise.reject(INCOMPATIBLE_RATEPLAN);
      }

      return this.createSessionCart(context, analytics).then(cartId => {
        let identifier = 'multiProp';
        if (this.isMixMatchEnabled) {
          identifier = 'mixAndMatch';
        }
        this.cartInfo = {
          id: cartId,
          timestamp: new Date().valueOf(),
          type: cartType ? cartType : this.getDefaultBusinessModel(),
          identifier,
        };
        saveCartInStorage(this.cartInfo);

        return this.cartInfo;
      });
    }

    return Promise.resolve(this.cartInfo);
  }

  public isPackageShoppingPath(): boolean {
    return Boolean(this.multiItemContext && this.multiItemContext.id && this.multiItemContext.packageType);
  }

  public getPackageType(): PackageType | null {
    if (this.isPackageShoppingPath()) {
      return this.multiItemContext?.packageType || null;
    }

    return null;
  }

  public isFlightPackage(): boolean {
    return (this.isPackageShoppingPath() && this.getPackageType()?.includes('F')) || false;
  }

  @action
  public updateSessionId(id?: string) {
    if (id && this.isPackageShoppingPath() && this.multiItemContext) {
      this.multiItemContext.id = id;
    }
  }

  public getExistingItems(roomIndex: number) {
    return this.items && this.items.filter(item => item.offerIndex === roomIndex);
  }

  private createSessionCart(context: ContextStore, analytics?: AnalyticsStore): Promise<Uuid> {
    return this.bexApiSource
      .createSessionCart({
        context: getBexApiContext(context),
        cartOptions: { ttl: CartTtl.SEVEN_DAYS },
      })
      .then(({ data: cartMutation }) => {
        if (analytics) {
          analytics.trackEvent('MultiProp.Create', 'MultiProp Cart Created', null, null, true);
        }

        return cartMutation?.createSessionCart?.id;
      });
  }

  private setError(error: any) {
    this.error = error;
  }

  @action
  public setGenericError() {
    this.errorMessage = this.genericError;
  }

  @action
  public setTtla(ttla: string) {
    this.ttla = ttla;
  }

  get genericError(): string {
    return `${this.l10n.formatText('hotels.infosite.errorMessage.text')} ${this.l10n.formatText('hotels.infosite.tryAgain.text')}`;
  }

  public isServerSide(): boolean {
    return typeof window === 'undefined'; // eslint-disable-line
  }

  private updateStatusWrapper(status: MultiItemStoreStatus, error?: string, userError?: MaxRoomsFailureReason): MultiItemStoreStatus {
    return updateStatus(this.logger, this.setError, status, this.l10n, error ? error.toString() : '', userError);
  }

  public buildContext(query: any = {}): void {
    this.multiItemContext = buildMultiItemContext(query);
  }

  @computed
  get isFreshCart() {
    return this.cartInfo && (!this.items || this.items.length === 0);
  }

  @computed
  get isTouchedCart() {
    return this.cartInfo && this.items && this.items.length > 0;
  }

  public isDisambigSearch(): boolean {
    return typeof this.referrer !== 'undefined' && this.referrer.includes('Disambig');
  }

  @computed
  get showMultiPropExperience() {
    return this.cartInfo && this.cartInfo.identifier !== 'mixAndMatch' && this.cartInfo.id;
  }

  @computed
  get showMixMatchExperience() {
    return this.cartInfo && this.cartInfo.id && this.isMixMatchEnabled;
  }

  public resetCart() {
    if (this.cartInfo) {
      this.cartInfo.type = this.getDefaultBusinessModel();
    }
    saveCartInStorage(this.cartInfo);
  }

  public removeMixAndMatchCart() {
    clearMixAndMatchCartFromStorage();
    this.cartInfo = {
      id: '',
      timestamp: 0,
      type: CollectionBusinessModel.EC,
      identifier: '',
    };
    this.itemCount = 0;
  }

  private calculateTotalPrice(): MoneyInput {
    const currency =
      this.items &&
      this.items[0] &&
      this.items[0].ratePlan &&
      this.items[0].ratePlan.totalPrice &&
      this.items[0].ratePlan.totalPrice.currencySymbol;
    const amount = this.items.reduce((total, item) => {
      if (!item || !item.ratePlan || !item.ratePlan || !item.ratePlan.totalPrice || !item.ratePlan.totalPrice.raw) {
        return total;
      }

      return total + item.ratePlan.totalPrice.raw;
    }, 0);

    return {
      amount,
      currency,
    };
  }

  public getDefaultBusinessModel() {
    return this.isMixMatchEnabled ? CollectionBusinessModel.NA : CollectionBusinessModel.EC;
  }

  private getCheckoutOptions(): CheckoutOptionInput[] {
    return (
      this.items &&
      this.items[0] && [
        {
          type: CheckoutOptionType.FENCING_ATTRIBUTES,
          value: this.items[0].fencingAttributes || '',
        },
      ]
    );
  }
}
